import React, { useState } from "react";
import DataTableHeader from "@/components/Datatable/components/DataTableHeader/DataTableHeader";
import DataTablePagination from "@/components/Datatable/components/DataTablePagination/DataTablePagination";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { DataTableProps } from "@/components/Datatable/types";
import { ChevronDown, ChevronRight } from "lucide-react";

const DataTable = <T extends object>({
  columns,
  data,
  totalCount,
  pageIndex,
  pageSize,
  onPageChange,
  sorting,
  onSortingChange,
  loading = false,
  error,
  rowKeyExtractor,
  noDataMessage = "No records found",
  emptyStateComponent,
  expandable = false,
  renderExpandedRow,
  onRowClick,
}: DataTableProps<T>) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set(),
  );

  const toggleRowExpansion = (rowKey: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  return (
    <div className="relative space-y-4">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/75 dark:bg-slate-950/90 z-50">
          <LoadingIndicator isFullScreen={false} />
        </div>
      )}

      {error && <div className="text-red-600 text-sm">Error: {error}</div>}

      <div className="rounded-md border border-gray-200 dark:border-slate-700 shadow-sm overflow-x-auto bg-white dark:bg-slate-800">
        <div className="relative w-full">
          <table className="w-full caption-bottom text-sm min-w-full text-gray-900 dark:text-slate-100">
            <DataTableHeader
              columns={columns}
              expandable={expandable}
              onSortingChange={onSortingChange}
              sorting={sorting}
            />

            <tbody className="[&_tr:last-child]:border-0">
              {data.length === 0 ? (
                <tr className="border-b border-gray-200 dark:border-slate-700 transition-colors hover:bg-gray-50 dark:hover:bg-slate-700">
                  <td
                    className={emptyStateComponent ? "p-0" : "p-10 text-center text-gray-500 dark:text-slate-400"}
                    colSpan={expandable ? columns.length + 1 : columns.length}
                  >
                    {emptyStateComponent || noDataMessage}
                  </td>
                </tr>
              ) : (
                data.map((item, index) => {
                  const rowKey = rowKeyExtractor
                    ? rowKeyExtractor(item, index)
                    : index;
                  const isExpanded = expandedRows.has(rowKey);

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={`border-b border-gray-200 dark:border-slate-700 transition-colors hover:bg-gray-50 dark:hover:bg-slate-700 ${expandable || onRowClick ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (expandable) toggleRowExpansion(rowKey);
                          if (onRowClick) onRowClick(item);
                        }}
                      >
                        {expandable && (
                          <td className="p-4 align-middle w-12 text-gray-500 dark:text-slate-400">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </td>
                        )}
                        {columns.map((column) => {
                          const rawCellKey = column.id || column.accessorKey;
                          const cellKey = String(rawCellKey);

                          const alignmentClass =
                            column.id === "actions"
                              ? "text-right"
                              : "text-left";

                          if (column.cell) {
                            return (
                              <td
                                className={`p-4 align-middle [&:has([role=checkbox])]:pr-0 ${alignmentClass}`}
                                key={cellKey}
                              >
                                {column.cell({ row: { original: item } })}
                              </td>
                            );
                          }

                          if (column.accessorKey) {
                            const typedKey = column.accessorKey as keyof T;
                            const cellValue = item[typedKey];
                            const displayValue =
                              cellValue != null ? String(cellValue) : "";

                            return (
                              <td
                                className={`p-4 align-middle [&:has([role=checkbox])]:pr-0 ${alignmentClass}`}
                                key={cellKey}
                              >
                                {displayValue}
                              </td>
                            );
                          }

                          return (
                            <td
                              className={`p-4 align-middle ${alignmentClass}`}
                              key={cellKey}
                            />
                          );
                        })}
                      </tr>
                      {expandable && isExpanded && renderExpandedRow && (
                        <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                          <td
                            className="p-0 align-middle max-w-0"
                            colSpan={columns.length + 1}
                          >
                            {renderExpandedRow(item)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DataTablePagination
        onPageChange={onPageChange}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
      />
    </div>
  );
};

export default DataTable;
